/**
 * CreateNewDropdown Component
 * Dropdown menu for creating new content items by type
 */

import { useNavigate } from 'react-router-dom'
import {
  Plus,
  ChevronDown,
  FileText,
  Hash,
  Image as ImageIcon,
  Table,
  Mail,
  Presentation,
  FolderPlus,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu'
import type { ContentItemType } from '@/lib/api/types'

// ============================================
// Types
// ============================================

interface CreateNewDropdownProps {
  currentFolderId?: string | null
  onCreateFolder?: () => void
  onCreateItem?: (type: ContentItemType) => void
  className?: string
}

interface ContentTypeOption {
  type: ContentItemType
  label: string
  description: string
  icon: React.ElementType
  color: string
}

// ============================================
// Constants
// ============================================

const CONTENT_TYPES: ContentTypeOption[] = [
  {
    type: 'Text',
    label: 'Text Element',
    description: 'Reusable text snippets',
    icon: FileText,
    color: 'text-blue-500',
  },
  {
    type: 'DataPoint',
    label: 'Data Point',
    description: 'Dynamic data values',
    icon: Hash,
    color: 'text-green-500',
  },
  {
    type: 'Image',
    label: 'Image',
    description: 'Managed images',
    icon: ImageIcon,
    color: 'text-purple-500',
  },
  {
    type: 'Table',
    label: 'Table',
    description: 'Data tables',
    icon: Table,
    color: 'text-amber-500',
  },
  {
    type: 'EmailSnippet',
    label: 'Email Snippet',
    description: 'Email components',
    icon: Mail,
    color: 'text-rose-500',
  },
  {
    type: 'SlideElement',
    label: 'Slide Element',
    description: 'Presentation slides',
    icon: Presentation,
    color: 'text-orange-500',
  },
]

// ============================================
// CreateNewDropdown Component
// ============================================

export function CreateNewDropdown({
  currentFolderId,
  onCreateFolder,
  onCreateItem,
  className,
}: CreateNewDropdownProps) {
  const navigate = useNavigate()

  const handleCreateItem = (type: ContentItemType) => {
    if (onCreateItem) {
      onCreateItem(type)
    } else {
      // Default navigation to new item page
      // Type is a route param, folderId is a query param
      const queryParams = currentFolderId ? `?folderId=${currentFolderId}` : ''
      navigate(`/content-hub/new/${type}${queryParams}`)
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          className={cn(
            'gap-1.5 rounded-full bg-neutral-900 dark:bg-white text-white dark:text-neutral-900',
            'hover:bg-neutral-800 dark:hover:bg-neutral-100',
            className
          )}
        >
          <Plus className="h-4 w-4" />
          <span>Create New</span>
          <ChevronDown className="h-3.5 w-3.5 ml-0.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        {/* Folder creation */}
        {onCreateFolder && (
          <>
            <DropdownMenuItem
              onClick={onCreateFolder}
              className="flex items-start gap-3 py-2 cursor-pointer"
            >
              <div className="mt-0.5">
                <FolderPlus className="h-5 w-5 text-amber-500" />
              </div>
              <div>
                <span className="font-medium block">New Folder</span>
                <span className="text-xs text-neutral-500 dark:text-neutral-400">
                  Organize your content
                </span>
              </div>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-neutral-500">
              Content Types
            </DropdownMenuLabel>
          </>
        )}

        {/* Content type options */}
        {CONTENT_TYPES.map((option) => {
          const Icon = option.icon
          return (
            <DropdownMenuItem
              key={option.type}
              onClick={() => handleCreateItem(option.type)}
              className="flex items-start gap-3 py-2 cursor-pointer"
            >
              <div className="mt-0.5">
                <Icon className={cn('h-5 w-5', option.color)} />
              </div>
              <div>
                <span className="font-medium block">{option.label}</span>
                <span className="text-xs text-neutral-500 dark:text-neutral-400">
                  {option.description}
                </span>
              </div>
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default CreateNewDropdown
